import { z } from 'zod';
import { networkBuild } from '@hoodmorning/config';
const SELECTED_ACCOUNT_CHAIN_ID = networkBuild.chainId;

const uint = z.string().max(78).regex(/^(0|[1-9][0-9]*)$/)
  .pipe(z.string().refine(value => BigInt(value) < (1n << 256n), { abort: true }));
const positive = uint.pipe(z.string().refine(value => BigInt(value) > 0n, { abort: true }));
const hash = z.string().regex(/^0x[0-9a-f]{64}$/i).transform(value => value.toLowerCase());
const index = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const leg = z.strictObject({ orderId: positive, legIndex: z.union([z.literal(0), z.literal(1)]) });
const noAuthority = {
  executionAuthorized: z.literal(false), signingAuthorized: z.literal(false), sendAuthorized: z.literal(false),
  resubmissionAuthorized: z.literal(false), nonceReleaseAuthorized: z.literal(false), accountingEligible: z.literal(false),
};

// Preserve raw timestamp precision and UUID case: both are SQLite BINARY keys.
export const externalOrderHistoryCursorSchema = z.strictObject({ admittedAt: z.iso.datetime(), attemptId: z.uuid() });
export type ExternalOrderHistoryCursor = z.infer<typeof externalOrderHistoryCursorSchema>;
const fill = z.strictObject({
  orderId: positive, legIndex: z.union([z.literal(0), z.literal(1)]), globalEpoch: positive,
  routeId: hash, routeRevision: positive, logIndex: index, revision: positive,
  actualInput: positive, actualNetOutput: positive, cumulativeInput: positive,
  cumulativeNetOutput: positive, complete: z.boolean(), sharedRemainingAfter: uint.nullable(),
});
const receipt = z.strictObject({
  revision: index.refine(value => value > 0), observationId: hash, recordedAt: z.iso.datetime(),
  outcome: z.enum(['success', 'reverted']), transactionHash: hash, blockNumber: uint, blockHash: hash,
  transactionIndex: index, headBlockNumber: uint, headBlockHash: hash,
  requiredConfirmations: z.number().int().min(2).max(256), gasUsed: positive, nativeGasCost: uint,
  fill: fill.nullable(),
});

/** Minimal historical display only. The API must restore complete attempt and
 * receipt evidence, including route/owner/quantity binding and producer identity.
 * This projection cannot prove current order status, finality or accounting. */
export const externalOrderHistoryItemSchema = z.strictObject({
  attemptId: z.uuid(), admittedAt: z.iso.datetime(), selected: leg, peer: leg.nullable(),
  transactionHash: hash, maximumThisFill: positive,
  dispatch: z.strictObject({ state: z.enum(['held', 'claimed']), claimedAt: z.iso.datetime().nullable(),
    transportReports: z.array(z.strictObject({ outcome: z.enum(['accepted', 'unknown', 'not-broadcast']),
      reportedAt: z.iso.datetime() })).max(3) }),
  receipt: receipt.nullable(), quarantined: z.boolean(),
  state: z.enum(['held', 'claimed-unresolved', 'recorded-fill', 'recorded-revert', 'quarantined']),
  ...noAuthority,
}).superRefine((item, ctx) => {
  const { dispatch, receipt: observed, peer, selected } = item;
  const expected = item.quarantined ? 'quarantined' : observed?.outcome === 'success' ? 'recorded-fill'
    : observed?.outcome === 'reverted' ? 'recorded-revert' : dispatch.state === 'held' ? 'held' : 'claimed-unresolved';
  const invalid = item.state !== expected
    || (peer ? peer.orderId === selected.orderId || peer.legIndex === selected.legIndex : selected.legIndex !== 0)
    || peer !== null && BigInt(peer.orderId) !== BigInt(selected.orderId) + (selected.legIndex === 0 ? 1n : -1n)
    || (dispatch.state === 'held') !== (dispatch.claimedAt === null)
    || dispatch.state === 'held' && (dispatch.transportReports.length !== 0 || observed !== null)
    || new Set(dispatch.transportReports.map(value => value.outcome)).size !== dispatch.transportReports.length
    || observed !== null && (observed.transactionHash !== item.transactionHash
      || (observed.outcome === 'success') !== (observed.fill !== null)
      || BigInt(observed.headBlockNumber) - BigInt(observed.blockNumber) + 1n < BigInt(observed.requiredConfirmations)
      || BigInt(observed.nativeGasCost) % BigInt(observed.gasUsed) !== 0n)
    || observed?.fill != null && (observed.fill.orderId !== selected.orderId || observed.fill.legIndex !== selected.legIndex
      || observed.fill.actualInput !== item.maximumThisFill
      || BigInt(observed.fill.cumulativeInput) < BigInt(observed.fill.actualInput)
      || BigInt(observed.fill.cumulativeNetOutput) < BigInt(observed.fill.actualNetOutput)
      || peer !== null && observed.fill.complete && observed.fill.sharedRemainingAfter !== '0'
      || (observed.fill.sharedRemainingAfter === null) !== (peer === null));
  if (invalid) ctx.addIssue({ code: 'custom', message: 'Inconsistent external order history' });
});
export type ExternalOrderHistoryItem = z.infer<typeof externalOrderHistoryItemSchema>;

function historyPageSchema<ChainId extends 4663 | typeof SELECTED_ACCOUNT_CHAIN_ID>(chainId:ChainId) {
return z.strictObject({
  schemaVersion: z.literal(1), version: z.literal('external-fixed-route-order.1'), chainId: z.literal(chainId),
  userId: z.string().min(1).max(128), registrationId: z.uuid(),
  items: z.array(externalOrderHistoryItemSchema).max(50), nextCursor: externalOrderHistoryCursorSchema.nullable(),
  ...noAuthority,
}).superRefine((page, ctx) => {
  const last = page.items.at(-1);
  const unordered = page.items.some((item, i) => {
    const prior = page.items[i - 1];
    return prior !== undefined && (item.admittedAt > prior.admittedAt
      || item.admittedAt === prior.admittedAt && item.attemptId >= prior.attemptId);
  });
  if (new Set(page.items.map(item => item.attemptId)).size !== page.items.length || unordered
    || page.nextCursor !== null && (!last || page.nextCursor.admittedAt !== last.admittedAt
      || page.nextCursor.attemptId !== last.attemptId)) {
    ctx.addIssue({ code: 'custom', message: 'Inconsistent external order history page' });
  }
});
}
export const externalOrderHistoryPageSchema = historyPageSchema(4663);
export type ExternalOrderHistoryPage = z.infer<typeof externalOrderHistoryPageSchema>;
/** Exact build-selected display domain. This does not translate old history or
 * establish execution, retry, finality or accounting authority. */
export const selectedExternalOrderHistoryPageSchema = historyPageSchema(SELECTED_ACCOUNT_CHAIN_ID);
export type SelectedExternalOrderHistoryPage = z.infer<typeof selectedExternalOrderHistoryPageSchema>;
